import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import { route } from 'preact-router';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';

// ─── Constants ────────────────────────────────────────────────────────────────

const SPORT_TYPES = ['Voetbal','Tennis','Hockey','Volleybal','Basketbal','Handbal','Zwemmen','Atletiek','Cycling','Overig'];
const SORT_OPTIONS = [
  { value: 'popular', label: '🔥 Populair' },
  { value: 'newest',  label: '🆕 Nieuwste' },
  { value: 'rating',  label: '⭐ Best beoordeeld' },
  { value: 'name',    label: '🔤 Naam' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Stars({ rating, max = 5, interactive, onRate, size = 16 }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={`display:inline-flex;gap:2px;cursor:${interactive ? 'pointer' : 'default'}`}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = interactive ? (hover || rating) > i : rating > i;
        return (
          <span
            key={i}
            style={`font-size:${size}px;color:${filled ? '#f59e0b' : 'var(--color-border)'};transition:color .1s`}
            onMouseEnter={() => interactive && setHover(i + 1)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onRate(i + 1)}
          >★</span>
        );
      })}
    </div>
  );
}

function CategoryBadge({ text, color = '#6366f1' }) {
  if (!text) return null;
  return (
    <span style={`font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;background:${color}20;color:${color};white-space:nowrap`}>
      {text}
    </span>
  );
}

function StatPill({ icon, value, label }) {
  return (
    <span style="font-size:11px;color:var(--color-text-muted);display:flex;align-items:center;gap:3px">
      <span>{icon}</span><strong style="color:var(--color-text)">{value}</strong> {label}
    </span>
  );
}

// ─── Template card ────────────────────────────────────────────────────────────

function TemplateCard({ tpl, onClick, onImport, importing }) {
  let steps = [];
  try { steps = JSON.parse(tpl.steps_json); } catch {}
  let tags = [];
  try { tags = JSON.parse(tpl.tags || '[]'); } catch {}

  return (
    <div
      onClick={() => onClick(tpl)}
      style="border:1px solid var(--color-border);border-radius:14px;padding:18px 20px;background:var(--color-surface);cursor:pointer;transition:all .18s;display:flex;flex-direction:column;gap:10px;position:relative"
      class="mkt-card"
    >
      {tpl.is_featured && (
        <div style="position:absolute;top:-1px;right:16px;font-size:10px;font-weight:700;background:#f59e0b;color:#fff;padding:2px 10px;border-radius:0 0 8px 8px">
          ⭐ Uitgelicht
        </div>
      )}

      {/* Header */}
      <div style="display:flex;align-items:flex-start;gap:10px">
        <div style="width:40px;height:40px;border-radius:10px;background:linear-gradient(135deg,#6366f1,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0">
          ⚙️
        </div>
        <div style="flex:1;min-width:0">
          <div style="font-size:14px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{tpl.name}</div>
          <div style="font-size:11px;color:var(--color-text-muted);margin-top:1px">{tpl.author_name || 'Anoniem'}</div>
        </div>
      </div>

      {/* Description */}
      {tpl.description && (
        <p style="font-size:12px;color:var(--color-text-muted);line-height:1.5;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;margin:0">
          {tpl.description}
        </p>
      )}

      {/* Badges */}
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        {tpl.category && <CategoryBadge text={tpl.category} color="#6366f1" />}
        {tpl.sport_type && <CategoryBadge text={tpl.sport_type} color="#10b981" />}
        {tags.slice(0, 2).map(tag => <CategoryBadge key={tag} text={tag} color="#94a3b8" />)}
      </div>

      {/* Stats */}
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
        <Stars rating={Math.round(tpl.avg_rating)} size={13} />
        {tpl.rating_count > 0 && <span style="font-size:11px;color:var(--color-text-muted)">({tpl.rating_count})</span>}
        <StatPill icon="📥" value={tpl.import_count} label="imports" />
        <StatPill icon="📋" value={steps.length} label="stappen" />
      </div>

      {/* Import button */}
      <button
        onClick={e => { e.stopPropagation(); onImport(tpl); }}
        disabled={importing === tpl.id || tpl.already_imported}
        style={`padding:8px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:${tpl.already_imported ? 'default' : 'pointer'};border:none;transition:all .15s;background:${tpl.already_imported ? 'var(--color-border)' : 'var(--color-primary)'};color:${tpl.already_imported ? 'var(--color-text-muted)' : '#fff'}`}
      >
        {tpl.already_imported ? '✓ Al geïmporteerd' : importing === tpl.id ? 'Importeren...' : '📥 Importeren'}
      </button>
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────────

function DetailModal({ tplId, onClose, onImported }) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [rating_saved, setRatingSaved] = useState(false);

  useEffect(() => {
    api(`/api/marketplace/${tplId}`).then(data => {
      setTpl(data);
      if (data.my_rating) { setRating(data.my_rating); setComment(data.my_comment || ''); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [tplId]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api(`/api/marketplace/${tplId}/import`, { method: 'POST' });
      onImported(tplId);
      setTpl(t => ({ ...t, already_imported: 1 }));
    } catch (e) {
      if (e.code === 'already_imported') setTpl(t => ({ ...t, already_imported: 1 }));
    } finally { setImporting(false); }
  };

  const handleRate = async (stars) => {
    setRating(stars);
    try {
      await api(`/api/marketplace/${tplId}/rate`, { method: 'POST', body: JSON.stringify({ rating: stars, comment }) });
      setRatingSaved(true);
      setTimeout(() => setRatingSaved(false), 2000);
    } catch {}
  };

  const handleSaveComment = async () => {
    try {
      await api(`/api/marketplace/${tplId}/rate`, { method: 'POST', body: JSON.stringify({ rating, comment }) });
      setRatingSaved(true);
      setTimeout(() => setRatingSaved(false), 2000);
    } catch {}
  };

  if (loading) return (
    <Modal isOpen title="Sjabloon laden..." onClose={onClose}>
      <div style="padding:40px;text-align:center;color:var(--color-text-muted)">Laden...</div>
    </Modal>
  );
  if (!tpl) return null;

  let steps = tpl.steps || [];
  let tags = tpl.tags || [];

  return (
    <Modal isOpen title={tpl.name} onClose={onClose}>
      {/* Meta */}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px">
        {tpl.category && <CategoryBadge text={tpl.category} color="#6366f1" />}
        {tpl.sport_type && <CategoryBadge text={tpl.sport_type} color="#10b981" />}
        {tags.map(t => <CategoryBadge key={t} text={t} color="#94a3b8" />)}
      </div>

      {tpl.description && (
        <p style="font-size:13px;color:var(--color-text-muted);line-height:1.6;margin-bottom:14px">{tpl.description}</p>
      )}

      <div style="display:flex;gap:16px;margin-bottom:16px;flex-wrap:wrap">
        <StatPill icon="📥" value={tpl.import_count} label="imports" />
        <StatPill icon="📋" value={steps.length} label="stappen" />
        <StatPill icon="👤" value={tpl.author_name || 'Anoniem'} label="" />
        <div style="display:flex;align-items:center;gap:6px">
          <Stars rating={Math.round(tpl.avg_rating)} size={14} />
          <span style="font-size:12px;color:var(--color-text-muted)">{tpl.avg_rating?.toFixed(1)} ({tpl.rating_count})</span>
        </div>
      </div>

      {/* Steps preview */}
      <div style="margin-bottom:20px">
        <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:8px">
          Stappen
        </div>
        <div style="border:1px solid var(--color-border);border-radius:10px;overflow:hidden">
          {steps.map((step, i) => (
            <div key={i} style="display:flex;gap:12px;padding:10px 14px;border-bottom:1px solid var(--color-border);align-items:flex-start">
              <div style="width:22px;height:22px;border-radius:50%;background:var(--color-primary);color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px">
                {i + 1}
              </div>
              <div style="flex:1;min-width:0">
                <div style="font-size:13px;font-weight:600;display:flex;align-items:center;gap:6px">
                  {step.title}
                  {step.is_approval_step && <span style="font-size:10px;background:#f59e0b20;color:#d97706;padding:1px 6px;border-radius:8px">✓ Goedkeuring</span>}
                </div>
                {step.description && <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px;line-height:1.4">{step.description}</div>}
                {step.tasks?.length > 0 && (
                  <div style="margin-top:4px;display:flex;flex-wrap:wrap;gap:4px">
                    {step.tasks.slice(0, 3).map((task, j) => (
                      <span key={j} style="font-size:10px;background:var(--color-bg);border:1px solid var(--color-border);padding:1px 7px;border-radius:8px;color:var(--color-text-muted)">
                        {typeof task === 'string' ? task : task.title}
                      </span>
                    ))}
                    {step.tasks.length > 3 && <span style="font-size:10px;color:var(--color-text-muted)">+{step.tasks.length - 3}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rating */}
      <div style="border:1px solid var(--color-border);border-radius:10px;padding:14px;margin-bottom:20px">
        <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:10px">
          Jouw beoordeling
        </div>
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px">
          <Stars rating={rating} interactive onRate={handleRate} size={24} />
          {rating_saved && <span style="font-size:12px;color:#10b981;font-weight:600">✓ Opgeslagen</span>}
        </div>
        <textarea
          class="form-input"
          value={comment}
          onInput={e => setComment(e.target.value)}
          rows={2}
          placeholder="Optioneel: toelichting bij je beoordeling..."
          style="font-size:12px;resize:none;margin-bottom:8px"
        />
        {comment && (
          <Button variant="secondary" size="sm" onClick={handleSaveComment}>Opslaan</Button>
        )}
      </div>

      {/* Recent ratings */}
      {tpl.recent_ratings?.length > 0 && (
        <div style="margin-bottom:20px">
          <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:8px">
            Beoordelingen
          </div>
          {tpl.recent_ratings.map((r, i) => (
            <div key={i} style="display:flex;gap:10px;padding:8px 0;border-bottom:1px solid var(--color-border)">
              <Stars rating={r.rating} size={12} />
              {r.comment && <span style="font-size:12px;color:var(--color-text-muted)">{r.comment}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Action */}
      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose}>Sluiten</Button>
        <Button
          variant={tpl.already_imported ? 'secondary' : 'primary'}
          onClick={handleImport}
          disabled={importing || tpl.already_imported}
        >
          {tpl.already_imported ? '✓ Al geïmporteerd' : importing ? 'Importeren...' : '📥 Importeer in mijn club'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Publish modal ────────────────────────────────────────────────────────────

function PublishModal({ onClose, onPublished }) {
  const [myTemplates, setMyTemplates] = useState([]);
  const [published, setPublished] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [sportType, setSportType] = useState('');
  const [tags, setTags] = useState('');
  const [descOverride, setDescOverride] = useState('');
  const [isAnon, setIsAnon] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      api('/api/templates'),
      api('/api/marketplace/my/published'),
    ]).then(([t, p]) => {
      setMyTemplates(Array.isArray(t) ? t : t.templates || []);
      setPublished(p);
    }).catch(() => {});
  }, []);

  const publishedIds = new Set(published.map(p => p.source_template_id));

  const handlePublish = async () => {
    if (!selectedId) { setError('Kies een sjabloon'); return; }
    setSaving(true); setError(null);
    try {
      await api('/api/marketplace/publish', {
        method: 'POST',
        body: JSON.stringify({
          template_id: parseInt(selectedId),
          sport_type: sportType || null,
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
          is_anonymous: isAnon,
          description_override: descOverride || null,
        }),
      });
      onPublished();
      onClose();
    } catch (e) {
      setError(e.message || 'Publiceren mislukt');
      setSaving(false);
    }
  };

  return (
    <Modal isOpen title="📤 Sjabloon publiceren" onClose={onClose}>
      <p style="font-size:13px;color:var(--color-text-muted);margin-bottom:16px;line-height:1.5">
        Deel een van jouw sjablonen met alle verenigingen op het platform. Andere clubs kunnen het importeren en aanpassen.
      </p>
      {error && <div class="alert alert-error mb-3">{error}</div>}

      <div style="margin-bottom:14px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Sjabloon *</label>
        <select class="form-input" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
          <option value="">— Kies een sjabloon —</option>
          {myTemplates.map(t => (
            <option key={t.id} value={t.id} disabled={publishedIds.has(t.id)}>
              {t.name}{publishedIds.has(t.id) ? ' (al gepubliceerd)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px">
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Sport type</label>
          <select class="form-input" value={sportType} onChange={e => setSportType(e.target.value)}>
            <option value="">— Algemeen —</option>
            {SPORT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Tags <span style="font-weight:400;color:var(--color-text-muted)">(komma-gescheiden)</span></label>
          <input class="form-input" value={tags} onInput={e => setTags(e.target.value)} placeholder="bv. jeugd, toernooi" />
        </div>
      </div>

      <div style="margin-bottom:14px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Extra beschrijving <span style="font-weight:400;color:var(--color-text-muted)">(optioneel)</span></label>
        <textarea class="form-input" value={descOverride} onInput={e => setDescOverride(e.target.value)} rows={2} placeholder="Aanvullende uitleg voor andere clubs..." style="resize:none;font-size:13px" />
        <AiAssist kind="marketplace_description" value={descOverride} onUse={setDescOverride}
          context={{ title: myTemplates.find(t => String(t.id) === String(selectedId))?.name || '' }} />
      </div>

      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;margin-bottom:20px">
        <input type="checkbox" checked={isAnon} onChange={e => setIsAnon(e.target.checked)} style="width:15px;height:15px" />
        Publiceer anoniem (clubnaam niet zichtbaar)
      </label>

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={handlePublish} disabled={saving}>{saving ? 'Publiceren...' : '📤 Publiceer'}</Button>
      </div>
    </Modal>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function MarketplaceView() {
  const { hasPermission } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [meta, setMeta] = useState({ categories: [], sports: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sport, setSport] = useState('');
  const [sort, setSort] = useState('popular');
  const [detailId, setDetailId] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [importing, setImporting] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');

  const canPublish = hasPermission('templates', 'update');
  const canImport = hasPermission('templates', 'create');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ sort });
    if (search)   params.set('search', search);
    if (category) params.set('category', category);
    if (sport)    params.set('sport', sport);
    try {
      const [all, feat, m] = await Promise.all([
        api(`/api/marketplace?${params}`),
        featured.length ? Promise.resolve(featured) : api('/api/marketplace?featured=true'),
        meta.categories.length ? Promise.resolve(meta) : api('/api/marketplace/meta'),
      ]);
      setTemplates(all);
      if (!featured.length) setFeatured(feat);
      if (!meta.categories.length) setMeta(m);
    } catch {}
    setLoading(false);
  }, [search, category, sport, sort]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleImport = async (tpl) => {
    if (tpl.already_imported) return;
    setImporting(tpl.id);
    setImportMsg(null);
    try {
      const res = await api(`/api/marketplace/${tpl.id}/import`, { method: 'POST' });
      setImportMsg({ ok: true, text: `✅ "${res.name}" is toegevoegd aan jouw sjablonen` });
      setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, already_imported: 1, import_count: t.import_count + 1 } : t));
    } catch (e) {
      if (e.code === 'already_imported') {
        setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, already_imported: 1 } : t));
      } else {
        setImportMsg({ ok: false, text: '❌ Importeren mislukt' });
      }
    } finally { setImporting(null); }
  };

  const [myPublished, setMyPublished] = useState([]);
  useEffect(() => {
    if (activeTab === 'mine') {
      api('/api/marketplace/my/published').then(setMyPublished).catch(() => {});
    }
  }, [activeTab]);

  const handleUnpublish = async (id) => {
    if (!confirm('Dit sjabloon uit de marktplaats verwijderen?')) return;
    try {
      await api(`/api/marketplace/${id}`, { method: 'DELETE' });
      setMyPublished(prev => prev.filter(t => t.id !== id));
    } catch {}
  };

  return (
    <div>
      <div class="page-header" style="margin-bottom:20px">
        <div>
          <h1 class="page-title">🛒 Sjablonen marktplaats</h1>
          <p style="font-size:13px;color:var(--color-text-muted);margin-top:2px">
            Browse en importeer processjablonen van andere verenigingen, of deel je eigen.
          </p>
        </div>
        {canPublish && (
          <Button variant="primary" size="sm" onClick={() => setShowPublish(true)}>
            📤 Publiceer sjabloon
          </Button>
        )}
      </div>

      {importMsg && (
        <div style={`padding:12px 16px;border-radius:10px;margin-bottom:16px;font-size:13px;background:${importMsg.ok ? '#10b98115' : '#ef444415'};color:${importMsg.ok ? '#10b981' : '#ef4444'};border:1px solid ${importMsg.ok ? '#10b98130' : '#ef444430'}`}>
          {importMsg.text}
          {importMsg.ok && <button onClick={() => route('/templates')} style="margin-left:12px;font-size:12px;color:var(--color-primary);background:none;border:none;cursor:pointer;text-decoration:underline">Naar sjablonen →</button>}
        </div>
      )}

      {/* Tabs */}
      <div style="display:flex;gap:2px;background:var(--color-border);border-radius:10px;padding:3px;margin-bottom:20px;width:fit-content">
        {[['browse','🛒 Bladeren'],['mine','📤 Mijn publicaties']].map(([id,label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={`padding:7px 16px;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:all .15s;background:${activeTab===id ? 'var(--color-surface)' : 'transparent'};color:${activeTab===id ? 'var(--color-text)' : 'var(--color-text-muted)'};box-shadow:${activeTab===id ? '0 1px 3px rgba(0,0,0,.1)' : 'none'}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'browse' && (
        <>
          {/* Featured */}
          {featured.length > 0 && !search && !category && !sport && (
            <div style="margin-bottom:28px">
              <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:12px">⭐ Uitgelichte sjablonen</div>
              <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px">
                {featured.slice(0, 3).map(tpl => (
                  <TemplateCard key={tpl.id} tpl={tpl} onClick={t => setDetailId(t.id)} onImport={handleImport} importing={importing} />
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:20px;align-items:center">
            <div style="position:relative;flex:1;min-width:200px;max-width:300px">
              <span style="position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:13px;color:var(--color-text-muted);pointer-events:none">🔍</span>
              <input class="form-input" value={search} onInput={e => setSearch(e.target.value)} placeholder="Zoek sjablonen..." style="padding-left:30px;height:34px;font-size:13px" />
            </div>
            <select class="form-input" value={category} onChange={e => setCategory(e.target.value)} style="height:34px;font-size:13px;min-width:140px">
              <option value="">Alle categorieën</option>
              {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select class="form-input" value={sport} onChange={e => setSport(e.target.value)} style="height:34px;font-size:13px;min-width:130px">
              <option value="">Alle sporten</option>
              {meta.sports.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style="display:flex;gap:4px;background:var(--color-border);border-radius:8px;padding:3px">
              {SORT_OPTIONS.map(o => (
                <button key={o.value} onClick={() => setSort(o.value)}
                  style={`padding:5px 10px;font-size:11px;font-weight:500;border:none;border-radius:6px;cursor:pointer;transition:all .15s;background:${sort===o.value ? 'var(--color-surface)' : 'transparent'};color:${sort===o.value ? 'var(--color-text)' : 'var(--color-text-muted)'}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style="border:1px solid var(--color-border);border-radius:14px;padding:18px;background:var(--color-surface)">
                  <div class="skeleton" style="height:40px;width:40px;border-radius:10px;margin-bottom:10px" />
                  <div class="skeleton" style="height:14px;width:70%;border-radius:4px;margin-bottom:6px" />
                  <div class="skeleton" style="height:11px;width:40%;border-radius:4px;margin-bottom:14px" />
                  <div class="skeleton" style="height:32px;border-radius:8px" />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div style="padding:60px;text-align:center;color:var(--color-text-muted)">
              <div style="font-size:40px;margin-bottom:12px">📭</div>
              <div style="font-size:14px">{search || category || sport ? 'Geen sjablonen gevonden voor deze filters' : 'De marktplaats is nog leeg — publiceer als eerste!'}</div>
            </div>
          ) : (
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px">
              {templates.map(tpl => (
                <TemplateCard key={tpl.id} tpl={tpl} onClick={t => setDetailId(t.id)} onImport={handleImport} importing={importing} />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'mine' && (
        <div>
          {myPublished.length === 0 ? (
            <div style="padding:60px;text-align:center;color:var(--color-text-muted)">
              <div style="font-size:40px;margin-bottom:12px">📤</div>
              <div style="font-size:14px;margin-bottom:16px">Je hebt nog geen sjablonen gepubliceerd</div>
              {canPublish && <Button variant="primary" size="sm" onClick={() => setShowPublish(true)}>Publiceer nu</Button>}
            </div>
          ) : (
            <div style="display:flex;flex-direction:column;gap:10px">
              {myPublished.map(p => (
                <div key={p.id} style="border:1px solid var(--color-border);border-radius:12px;padding:16px 18px;display:flex;align-items:center;gap:14px;background:var(--color-surface)">
                  <div style="flex:1;min-width:0">
                    <div style="font-size:14px;font-weight:600">{p.name}</div>
                    <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px;display:flex;gap:12px">
                      <span>📥 {p.import_count} imports</span>
                      <span>⭐ {p.avg_rating?.toFixed(1) || '—'} ({p.rating_count})</span>
                      <span>📅 {parseServerDate(p.published_at).toLocaleDateString('nl-NL')}</span>
                    </div>
                  </div>
                  {p.category && <CategoryBadge text={p.category} color="#6366f1" />}
                  {p.sport_type && <CategoryBadge text={p.sport_type} color="#10b981" />}
                  {p.is_featured ? <span style="font-size:11px;background:#f59e0b20;color:#d97706;padding:2px 8px;border-radius:10px;font-weight:600">⭐ Uitgelicht</span> : null}
                  <button onClick={() => handleUnpublish(p.id)} style="padding:6px 12px;font-size:12px;border:1px solid var(--color-danger,#dc2626);border-radius:8px;background:transparent;color:var(--color-danger,#dc2626);cursor:pointer">
                    Depubliceer
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail modal */}
      {detailId && (
        <DetailModal
          tplId={detailId}
          onClose={() => setDetailId(null)}
          onImported={(id) => {
            setTemplates(prev => prev.map(t => t.id === id ? { ...t, already_imported: 1, import_count: t.import_count + 1 } : t));
            setImportMsg({ ok: true, text: '✅ Sjabloon geïmporteerd in jouw club' });
            setDetailId(null);
          }}
        />
      )}

      {/* Publish modal */}
      {showPublish && (
        <PublishModal
          onClose={() => setShowPublish(false)}
          onPublished={() => { fetchTemplates(); if (activeTab === 'mine') api('/api/marketplace/my/published').then(setMyPublished).catch(() => {}); }}
        />
      )}

      <style>{`
        .mkt-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,.15); transform: translateY(-2px); border-color: var(--color-primary); }
      `}</style>
    </div>
  );
}
